import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Modal,
  Space,
  Tag,
  Typography,
  message
} from 'antd';
import type { Route } from '../../types';
import {
  useCreateRoutePenaltyMutation,
  useFinalizeOperationalCompletionMutation,
  useGetOperationalCompletionQuery
} from '../../store/api/routesApi';
import {
  useGetCarrierAgreementByRouteQuery,
  useGetCarrierAgreementPenaltiesQuery,
  type CarrierAgreementPenaltyRow
} from '../../store/api/carrierAgreementsApi';
import { useCreateRouteExpenseMutation, useGetRouteExpensesQuery } from '../../store/api/financeApi';
import { PENALTY_TYPE_LABELS } from '../../utils/penaltyTypeLabels';
import {
  completionDraftToExpenseCreates,
  completionDraftToPenaltyCreates,
  useRouteCompletionDraft,
  type CompletionExpenseDraft,
  type CompletionPenaltyDraft
} from '../operationalDomain';
import { buildCompletionSnapshotFromFinalize } from '../operationalDomain/completion/completionSnapshot';
import RouteExpenseForm from '../finance/components/shared/RouteExpenseForm';
import { routeExpenseStatusLabel, routeExpenseStatusTagColor } from '../finance/constants/routeExpenseStatus';
import { routeExpenseTypeLabel } from '../finance/constants/routeExpenseTypes';

const { Text } = Typography;
const { TextArea } = Input;

type Props = {
  open: boolean;
  route: Route;
  onClose: () => void;
  onCompleted?: () => void;
};

export const RouteCompletionModal: React.FC<Props> = ({ open, route, onClose, onCompleted }) => {
  const { data: completionState, refetch: refetchCompletion } = useGetOperationalCompletionQuery(
    route.id,
    { skip: !open }
  );
  const locked = completionState?.locked ?? false;
  const snapshot = completionState?.snapshot ?? null;

  const [createPenalty, { isLoading: penaltyLoading }] = useCreateRoutePenaltyMutation();
  const [createExpense, { isLoading: expenseLoading }] = useCreateRouteExpenseMutation();
  const [finalizeCompletion, { isLoading: finalizeLoading }] = useFinalizeOperationalCompletionMutation();

  const { data: agreement } = useGetCarrierAgreementByRouteQuery(route.id, { skip: !open });
  const { data: penaltyRows = [], isLoading: penaltiesLoading } = useGetCarrierAgreementPenaltiesQuery(
    agreement?.id as number,
    { skip: !open || !agreement?.id }
  );
  const { data: expensesPayload, refetch: refetchExpenses } = useGetRouteExpensesQuery(
    { routeId: route.id, includeDraft: true, includeRejected: true },
    { skip: !open }
  );
  const expenses = useMemo(
    () => expensesPayload?.expenses ?? [],
    [expensesPayload?.expenses]
  );

  const manualPenaltyConfigs = useMemo(
    () => penaltyRows.filter((p) => p.requiresManualInput),
    [penaltyRows]
  );

  const readonly = locked;

  const {
    draft,
    setPenaltyEnabled,
    patchPenaltyField,
    setLogistComment,
    addExpense,
    patchExpense,
    removeExpense
  } = useRouteCompletionDraft({
    open: open && !locked,
    penaltyConfigs: manualPenaltyConfigs,
    expenses
  });

  const frozenSnapshot = useMemo(() => {
    if (!snapshot) return null;
    return buildCompletionSnapshotFromFinalize(snapshot, [], expenses);
  }, [snapshot, expenses]);

  const handleFinalize = async () => {
    if (locked) return;
    if (draft.validationState === 'invalid') {
      message.warning('Исправьте ошибки перед завершением');
      return;
    }
    const comment = draft.logistComment.trim();
    if (comment.length < 3) {
      message.warning('Комментарий логиста обязателен (минимум 3 символа)');
      return;
    }
    try {
      const penaltyIds: number[] = [];
      for (const payload of completionDraftToPenaltyCreates(draft, route.id)) {
        const created = await createPenalty(payload).unwrap();
        if (created?.id) penaltyIds.push(created.id);
      }

      const expenseIds: number[] = draft.expenses
        .filter((e) => e.expenseId != null && !e.isNew)
        .map((e) => e.expenseId as number);

      for (const payload of completionDraftToExpenseCreates(draft, route.id)) {
        const created = await createExpense(payload).unwrap();
        if (created?.id) expenseIds.push(created.id);
      }

      await finalizeCompletion({
        routeId: route.id,
        logistComment: comment,
        penaltyIds,
        expenseIds,
        warnings: draft.warnings.map((w) => w.message)
      }).unwrap();

      message.success('Маршрут завершён. Расходы отправлены бухгалтеру на подтверждение.');
      await refetchCompletion();
      await refetchExpenses();
      onCompleted?.();
      onClose();
    } catch (e) {
      const err = e as { data?: { error?: string }; message?: string };
      message.error(err?.data?.error ?? err?.message ?? 'Ошибка завершения маршрута');
    }
  };

  const loading = penaltyLoading || expenseLoading || finalizeLoading;

  return (
    <Modal
      open={open}
      title={readonly ? 'Маршрут завершён' : 'Завершение маршрута'}
      onCancel={onClose}
      onOk={readonly ? onClose : () => void handleFinalize()}
      confirmLoading={loading}
      okText={readonly ? 'Закрыть' : 'Завершить маршрут'}
      cancelButtonProps={readonly ? { style: { display: 'none' } } : undefined}
      width={720}
      destroyOnClose={false}
    >
      {readonly && frozenSnapshot ? (
        <Alert
          type="success"
          showIcon
          message="Маршрут завершён"
          description={`Зафиксировано ${new Date(frozenSnapshot.completedAt).toLocaleString('ru-RU')}. Изменение штрафов и расходов недоступно.`}
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          Штрафы и расходы фиксируются при завершении. Расходы создаются как черновики и уходят бухгалтеру
          (задача EXPENSE_CONFIRMATION) — подтверждение и P&L выполняет finance domain.
        </Text>
      )}

      <div style={{ marginBottom: 12 }}>
        <Text className="execution-workspace__muted" style={{ fontSize: 11 }}>
          Комментарий логиста
        </Text>
        <TextArea
          rows={2}
          placeholder="Итоги рейса, проблемы, удержания"
          value={readonly && frozenSnapshot ? frozenSnapshot.logistComment : draft.logistComment}
          onChange={(e) => setLogistComment(e.target.value)}
          disabled={readonly}
        />
      </div>

      {!readonly && draft.readinessState === 'blocked' ? (
        <Text type="danger" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          Заполните обязательные поля.
        </Text>
      ) : null}

      {penaltiesLoading ? <Text>Загрузка штрафов договора…</Text> : null}
      {!readonly && !penaltiesLoading && manualPenaltyConfigs.length === 0 ? (
        <Text type="secondary">Нет штрафов с ручным вводом по договору перевозчика.</Text>
      ) : null}

      {!readonly ? (
        <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size={12}>
          {draft.penalties.map((p) => (
            <PenaltyDraftRow
              key={p.penaltyId}
              draftPenalty={p}
              config={manualPenaltyConfigs.find((c) => c.id === p.penaltyId)}
              onEnabledChange={(enabled) => setPenaltyEnabled(p.penaltyId, enabled)}
              onPatch={(patch) => patchPenaltyField(p.penaltyId, patch)}
            />
          ))}
        </Space>
      ) : null}

      <div style={{ marginTop: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Дополнительные расходы
        </Text>
        {!readonly ? (
          <Alert
            type="info"
            showIcon
            message="Отправка бухгалтеру"
            description="После сохранения расходы получают статус «Отправлено бухгалтеру» и попадают в задачу на подтверждение."
            style={{ marginBottom: 10 }}
          />
        ) : null}
        {readonly && frozenSnapshot ? (
          frozenSnapshot.expenses.length === 0 ? (
            <Text type="secondary">Нет зафиксированных расходов</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {frozenSnapshot.expenses.map((e) => (
                <Text key={e.id}>
                  {routeExpenseTypeLabel(e.type)}: {e.amount.toLocaleString('ru-RU')} ₽ —{' '}
                  {routeExpenseStatusLabel(e.status, 'logist', { persisted: true })}
                </Text>
              ))}
            </Space>
          )
        ) : (
          <>
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              {draft.expenses.map((e, idx) => (
                <ExpenseDraftRow
                  key={e.expenseId ?? `new-${idx}`}
                  draft={e}
                  onPatch={(patch) => patchExpense(idx, patch)}
                  onRemove={() => removeExpense(idx)}
                />
              ))}
            </Space>
            <Button type="dashed" onClick={addExpense} style={{ marginTop: 8 }}>
              Добавить расход
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

type PenaltyRowProps = {
  draftPenalty: CompletionPenaltyDraft;
  config?: CarrierAgreementPenaltyRow;
  onEnabledChange: (enabled: boolean) => void;
  onPatch: (patch: Partial<{ days: number | null; amount: number | null; comment: string }>) => void;
};

const PenaltyDraftRow = React.memo(function PenaltyDraftRow({
  draftPenalty: p,
  config,
  onEnabledChange,
  onPatch
}: PenaltyRowProps) {
  const typeU = p.penaltyType;
  const label = config?.name ?? p.name;

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
      <Checkbox checked={p.enabled} onChange={(e) => onEnabledChange(e.target.checked)}>
        <Text strong>{label}</Text>
        <Text type="secondary" style={{ marginLeft: 8 }}>
          ({PENALTY_TYPE_LABELS[typeU as keyof typeof PENALTY_TYPE_LABELS] ?? typeU})
        </Text>
      </Checkbox>
      {p.enabled ? (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          {p.perDay ? (
            <InputNumber
              placeholder="Дней"
              min={1}
              value={p.days ?? undefined}
              onChange={(v) => onPatch({ days: v })}
              style={{ width: '100%' }}
            />
          ) : null}
          {typeU === 'FIXED' ? (
            <InputNumber
              placeholder="Сумма"
              min={0}
              value={p.amount ?? undefined}
              onChange={(v) => onPatch({ amount: v })}
              style={{ width: '100%' }}
            />
          ) : null}
          {p.requiresComment ? (
            <Input
              placeholder="Комментарий"
              value={p.comment ?? ''}
              onChange={(e) => onPatch({ comment: e.target.value })}
            />
          ) : null}
        </Space>
      ) : null}
    </div>
  );
});

type ExpenseRowProps = {
  draft: CompletionExpenseDraft;
  onPatch: (patch: Partial<CompletionExpenseDraft>) => void;
  onRemove: () => void;
};

const ExpenseDraftRow: React.FC<ExpenseRowProps> = ({ draft, onPatch, onRemove }) => {
  if (!draft.isNew) {
    return (
      <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
        <Space>
          <Text>
            {routeExpenseTypeLabel(draft.type)}: {Number(draft.amountWithVAT).toLocaleString('ru-RU')} ₽
          </Text>
          <Tag color={routeExpenseStatusTagColor(draft.status)}>
            {routeExpenseStatusLabel(draft.status, 'logist', { persisted: true })}
          </Tag>
        </Space>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
      <RouteExpenseForm
        compact
        showInvoice={false}
        values={draft}
        onChange={(v) => onPatch(v as Partial<CompletionExpenseDraft>)}
      />
      <Button type="link" danger onClick={onRemove} style={{ paddingLeft: 0 }}>
        Удалить
      </Button>
    </div>
  );
};

export default RouteCompletionModal;
